import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import nurseryService from '../../services/nursery';
import { stockItemLabel } from '../../utils/nurseryRowSlots';
import { isPlantItem } from '../../types/nursery';
import type { LotStage, NurseryLocation, NurseryLot, NurseryStockItem } from '../../types/nursery';

const STAGES: LotStage[] = ['sown', 'seedling', 'transplanted', 'grafted', 'budded', 'growing', 'potted', 'ready'];

interface Props {
  location: NurseryLocation;
  /** "Row 12 left · position 30", or the location's own name. */
  title: string;
  /** Lots already standing here. */
  lotsHere: NurseryLot[];
  stockItems: NurseryStockItem[];
  onPlaced: (note: string) => void;
  onCancel: () => void;
}

type Tree = Pick<NurseryLot, 'cultivar_id' | 'rootstock_id' | 'tree_type_id'>;
const sameTree = (a: Tree, b: Tree) =>
  a.cultivar_id === b.cultivar_id && (a.rootstock_id ?? null) === (b.rootstock_id ?? null) && a.tree_type_id === b.tree_type_id;

/**
 * Put a stock item into one location, from the locations screen: the same
 * lot the full Place a Lot form makes, with the place already chosen.
 *
 * A row position holds one stock item. More lots of it can join as they come
 * ready, so a filled position offers only the tree already standing there.
 * A seed bed, a polyhouse or a despatch area holds anything.
 */
export default function PlaceLotPanel({ location, title, lotsHere, stockItems, onPlaced, onCancel }: Props) {
  const { t } = useTranslation('nursery', { keyPrefix: 'PlaceLotPanel' });
  const isPosition = location.row_number != null && location.sequence_number != null && !location.is_despatch_area;
  const standing = lotsHere.filter(l => l.status === 'active' && l.current_count > 0);
  const held = isPosition && standing.length ? standing[0] : null;
  const mixed = held != null && standing.some(l => !sameTree(l, held));
  // A lot of trees is placed as a plant; merchandise has no lots yet.
  const plants = stockItems.filter(isPlantItem);
  const choices = held ? (mixed ? [] : plants.filter(i => sameTree(i, held))) : plants;

  const [stockItemId, setStockItemId] = useState<number | ''>(held && choices.length ? choices[0].id : '');
  const [count, setCount] = useState<number | ''>('');
  const [stage, setStage] = useState<LotStage>('sown');
  const [plantDate, setPlantDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const item = choices.find(i => i.id === stockItemId);
  const fullForm = `/nursery/lots/new?location_id=${location.id}`;

  const place = async () => {
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.createLot({
        cultivar_id: item.cultivar_id,
        rootstock_id: item.rootstock_id,
        tree_type_id: item.tree_type_id,
        location_id: location.id,
        current_stage: stage,
        initial_count: count === '' ? 0 : count,
        plant_date: plantDate || null,
        record_origin: true,
      });
      if (res.success) onPlaced(t('placedNote', { n: count === '' ? 0 : count, item: stockItemLabel(item), place: title }));
      else setError((res as { error?: { message?: string } }).error?.message || t('placeFailed'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('placeFailed'));
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white';

  return (
    <div className="bg-primary-50/50 border border-primary-200 rounded-lg px-3 py-3 mt-2 text-sm">
      <p className="font-semibold text-neutral-800 break-words">{t('heading', { place: title })}</p>

      {lotsHere.length > 0 && (
        <ul className="mt-1 text-neutral-600">
          {lotsHere.map(l => (
            <li key={l.id} className="break-words">
              {t('hereNow')}{' '}
              <Link to={`/nursery/lots/${l.id}`} className="text-primary-700 hover:underline">
                {stockItemLabel(l)}
              </Link>
              {' '}· {l.current_count} {t(`stages.${l.current_stage}`, { defaultValue: l.current_stage })}
            </li>
          ))}
        </ul>
      )}

      {held && !mixed && choices.length > 0 && (
        <p className="mt-2 text-neutral-600 break-words">
          {t('holdsOne', { item: stockItemLabel(held) })}
        </p>
      )}

      {mixed ? (
        <p className="mt-2 text-amber-800 break-words">
          {t('mixed')}
        </p>
      ) : held && choices.length === 0 ? (
        <p className="mt-2 text-amber-800 break-words">
          {t('notStockItem')}
        </p>
      ) : plants.length === 0 ? (
        <p className="mt-2 text-neutral-600 break-words">
          <Trans t={t} i18nKey="noStockItems" components={{ link: <Link to="/nursery/stock-items/new" className="text-primary-700 hover:underline" /> }} />
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 mt-3 sm:grid-cols-4">
          <div className="col-span-2 sm:col-span-4">
            <label htmlFor="place-stock-item" className="block font-medium text-neutral-700 mb-1">{t('stockItem')}</label>
            <select id="place-stock-item" value={stockItemId} className={field}
              onChange={e => setStockItemId(e.target.value ? Number(e.target.value) : '')}>
              {!held && <option value="">{t('chooseStockItem')}</option>}
              {choices.map(i => <option key={i.id} value={i.id}>{stockItemLabel(i)}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="place-count" className="block font-medium text-neutral-700 mb-1">{t('count')}</label>
            <input id="place-count" type="number" min="0" value={count} className={field}
              onChange={e => setCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))} />
          </div>
          <div>
            <label htmlFor="place-stage" className="block font-medium text-neutral-700 mb-1">{t('stage')}</label>
            <select id="place-stage" value={stage} onChange={e => setStage(e.target.value as LotStage)} className={field}>
              {STAGES.map(s => <option key={s} value={s}>{t(`stages.${s}`)}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label htmlFor="place-date" className="block font-medium text-neutral-700 mb-1">{t('plantDate')}</label>
            <input id="place-date" type="date" value={plantDate} onChange={e => setPlantDate(e.target.value)} className={field} />
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-red-700 break-words">{error}</p>}

      <div className="flex items-center gap-2 flex-wrap mt-3">
        {choices.length > 0 && (
          <Button variant="primary" size="sm" onClick={place} disabled={busy || !item}>
            {busy ? t('placing') : t('placeLot')}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>{t('cancel')}</Button>
        {choices.length > 0 && (
          <Link to={fullForm} className="text-primary-700 hover:underline">{t('moreOptions')}</Link>
        )}
      </div>
    </div>
  );
}
